import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Navbar } from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import "./About.css";

const AboutPage = () => {
	useEffect(() => {
		window.scrollTo({ top: 0 });
	}, []);

	return (
		<>
			<Helmet>
				<title>About - Aire Studio</title>
				<meta name="description" content="Learn about Aire Studio -- crafting bespoke balloon arrangements and curated gifts with purpose and precision." />
			</Helmet>

			<Navbar />

			<section className="about-hero">
				<img
					src="https://images.pexels.com/photos/7221143/pexels-photo-7221143.jpeg?auto=compress&cs=tinysrgb&w=1920&h=600&fit=crop"
					alt="About Aire Studio"
					className="about-hero-img"
				/>
				<div className="about-hero-overlay" />
				<div className="about-hero-content">
					<motion.h1
						className="about-hero-title"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
					>
						About Us
					</motion.h1>
				</div>
			</section>

			<section className="about-story section-padding">
				<div className="container">
					<motion.div
						className="about-story-layout"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.3 }}
					>
						<div className="about-story-image">
							<img
								src="https://images.pexels.com/photos/7233962/pexels-photo-7233962.jpeg?auto=compress&cs=tinysrgb&w=800&h=1000&fit=crop"
								alt="Nakita Narayan - Aire Studio founder"
							/>
						</div>
						<div className="about-story-text">
							<p className="about-story-label">Meet the Founder</p>
							<h2 className="about-story-title">Hi, I'm Nakita Narayan</h2>
							<p className="about-story-body">
								I'm a mortgage broker, proud mom of two amazing little ones (ages 3 and 5), and the heart behind Aire Studio. I've always had a hustler's spirit and knew I wanted to build something of my own — I just wasn't sure what it would be at first.
							</p>
							<p className="about-story-body">
								That changed when I realized how much I was spending on balloons for my kids' birthdays. It clicked: why not create something beautiful myself — and for others too? That's how the idea for my balloon and gift basket business was born.
							</p>
							<p className="about-story-body">
								With over 10 years in the finance industry, I've had the opportunity to build strong relationships with many realtors and professionals. Through those connections, I saw a consistent need for thoughtful, high-quality, and personalized gifts — especially to celebrate client milestones.
							</p>
							<p className="about-story-body">
								What started as a simple idea quickly turned into a passion for designing custom gift baskets, not just for real estate closings, but also for corporate events, celebrations, and special occasions of all kinds.
							</p>
							<p className="about-story-body">
								When I shared my vision with my husband, he jumped right in — even building me my own workspace in our garage so I could bring it to life.
							</p>
							<p className="about-story-body">
								Today, I'm proud to pour my creativity into crafting stunning balloon installations and custom gift baskets for clients, friends, and family. Every piece is created with intention, attention to detail, and a genuine love for celebrating life's special moments.
							</p>
							<p className="about-story-body">
								Thank you for being here — I can't wait to help make your next occasion unforgettable!
							</p>
						</div>
					</motion.div>
				</div>
			</section>

			<section className="about-stats-section">
				<div className="container">
					<div className="about-stats-grid">
						<div className="about-stat-card">
							<span className="about-stat-number">2,500+</span>
							<span className="about-stat-label">Creations Delivered</span>
						</div>
						<div className="about-stat-card">
							<span className="about-stat-number">98%</span>
							<span className="about-stat-label">Client Satisfaction</span>
						</div>
						<div className="about-stat-card">
							<span className="about-stat-number">7+</span>
							<span className="about-stat-label">Years of Artistry</span>
						</div>
						<div className="about-stat-card">
							<span className="about-stat-number">500+</span>
							<span className="about-stat-label">Events Styled</span>
						</div>
					</div>
				</div>
			</section>

			<section className="about-values section-padding">
				<div className="container">
					<div className="about-values-header">
						<p className="about-values-label">What We Stand For</p>
						<h2 className="about-values-title">Our Values</h2>
					</div>
					<div className="about-values-grid">
						<div className="about-value-card">
							<h3 className="about-value-name">Artistry</h3>
							<p className="about-value-desc">
								Every creation is a work of art. We approach each project with the same
								dedication a sculptor brings to their medium -- shaping, refining, perfecting.
							</p>
						</div>
						<div className="about-value-card">
							<h3 className="about-value-name">Intentionality</h3>
							<p className="about-value-desc">
								Nothing is accidental. From color palettes to proportions, each choice serves
								a purpose and contributes to a cohesive, memorable experience.
							</p>
						</div>
						<div className="about-value-card">
							<h3 className="about-value-name">Dedication</h3>
							<p className="about-value-desc">
								We treat every occasion as if it were our own. The details matter, the timeline
								matters, and your vision matters most of all.
							</p>
						</div>
					</div>
				</div>
			</section>

			<Footer />
		</>
	);
};

export default AboutPage;
